import { Ref, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { useTranslation } from "@coworker/locales";
import MfaqInfoPanel from "@coworker/apprestructured/src/task-management/components/MfaqInfoPanel/MfaqInfoPanel";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import profilePreferences from "@coworker/enums/profilePreferences";
import { FilterValue } from "@coworker/apprestructured/src/shared/filters/types/filter";
import { usePeriodFilter } from "@coworker/app/src/components/MFAQ/hooks/usePeriodFilter";
import { useLocationFilter } from "@coworker/app/src/components/MFAQ/hooks/useLocationFilter";
import { getPeriodTimestampsFromFilters } from "@coworker/app/src/components/MFAQ/helper";
import { useMFAQ } from "@coworker/app/src/components/MFAQ/hooks/useMFAQsService";
import { calculateNumberOfVotes } from "@coworker/app/src/components/MFAQ/QuestionDetailsPage/QuestionsDetailPage.helper";
import EmptyObject from "@coworker/apprestructured/src/task-management/types/EmptyObject";

export type MfaqInfoPanelCreatorFragmentConfigProps = EmptyObject;

type MfaqInfoPanelCreatorFragmentProps = {
  configProps: MfaqInfoPanelCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function getSelectedLabel(
  options: {
    title: string;
    value?: unknown;
    options?: { title: string; value?: unknown }[];
  }[],
  selectedValue: unknown
): string {
  for (const opt of options) {
    if ("options" in opt && opt.options) {
      const found = opt.options.find((o) => o.value === selectedValue);
      if (found) return found.title;
    } else if (opt.value === selectedValue) {
      return opt.title;
    }
  }
  return options[0]?.title ?? "";
}

function MfaqInfoPanelCreatorFragment({
  commonProps,
  ref,
}: MfaqInfoPanelCreatorFragmentProps) {
  const { task } = commonProps;
  const questionId = getTypedValue<string>(
    getByPath(task, "details.question_id")
  );

  const [mfaqFilterValues] = useUserPreference<FilterValue[]>(
    profilePreferences.MFAQ_FILTERS,
    []
  );

  const periodFilter = usePeriodFilter(mfaqFilterValues);
  const periodLabel = getSelectedLabel(
    periodFilter.options,
    mfaqFilterValues.find((f) => f.filterId === "period")?.value ??
      periodFilter.defaultValue.value
  );

  const locationFilter = useLocationFilter();
  const locationValue = (mfaqFilterValues.find((f) => f.filterId === "location")
    ?.value ?? locationFilter.defaultValue.value) as string;
  const locationLabel = getSelectedLabel(locationFilter.options, locationValue);

  const [startDate, endDate] = getPeriodTimestampsFromFilters(mfaqFilterValues);
  const resolvedQuestionId = isLoading(questionId) ? undefined : questionId;
  const { data: question, isLoading: isMfaqLoading } = useMFAQ(
    resolvedQuestionId,
    startDate,
    endDate
  );
  const upvotes = question
    ? calculateNumberOfVotes(question, locationValue)
    : 0;

  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    valid: true,
  }));

  if (isLoading(questionId) || isMfaqLoading) {
    return <LoadingCreatorFragment />;
  }

  return (
    <MfaqInfoPanel
      label={t("mfaqGraphUpvotesLabel")}
      filterLabel={periodLabel}
      upvotes={upvotes}
      location={locationLabel}
    />
  );
}

export default MfaqInfoPanelCreatorFragment;
